import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import { AddCircleOutline, Block, Delete, DeleteOutline, Edit, PictureAsPdf, Refresh } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { toast } from 'react-toastify';
import TableView from '../../components/TableView';
import ConfirmDialog from '../../components/ConfirmDialog';
import ExportMenuButton from '../../components/ExportMenuButton';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { useData } from '../../hooks/useData';
import { periodRequestConfig } from '../../hooks/useDateRange';
import { Util } from '../../util/Util';
import { LocalStorageKeys } from '../../util/constants';
import type { VoucherRow } from './voucherTypes';
import { voucherColumns } from './voucherColumns';
import { useVoucherDelete } from './useVoucherDelete';
import VoucherModal from './VoucherModal';
import VoucherReport from './VoucherReport';
import VoucherBatchReport from './VoucherBatchReport';
import { VOUCHER_STATUSES, voucherStatusI18nKey } from './voucherTypes';
import { canAmend, canRecord } from '../../util/access';

const Voucher = () => {
  const { api, access } = useAuth();
  const { queueReRun, t, setLoadingMessage } = useUI();
  const {
    data,
    lastUpdate,
    loadFromIndexedDB,
    saveFromServer,
    dateRange: period,
    setDateRange: setPeriod,
    loading,
    setLoading,
  } = useData<VoucherRow>(LocalStorageKeys.VOUCHER_RECORDS);
  const [postOpen, setPostOpen] = useState(false);
  const [editing, setEditing] = useState<VoucherRow | null>(null);
  const [toView, setToView] = useState<VoucherRow | null>(null);
  const [toViewBatch, setToViewBatch] = useState<string | null>(null);
  const [toRevoke, setToRevoke] = useState<VoucherRow | null>(null);
  const [toRevokeBatch, setToRevokeBatch] = useState<string | null>(null);
  const [toDeleteBatch, setToDeleteBatch] = useState<string | null>(null);
  const firstFetchRef = useRef(false);
  const canPost = canRecord(access, 'voucher');
  const canEdit = canAmend(access, 'voucher')
    || canRecord(access, 'voucher');
  const toastIdRef = useRef<string | number>(-1);

  const fetchData = async () => {
    setLoading(true);
    const res = await api.get('/vouchers', periodRequestConfig(period));
    if (res?.status === 200) {
      const rows = Array.isArray(res.data) ? res.data : res.data?.items || [];
      saveFromServer(rows, period);
      toast.update(toastIdRef.current, {
        render: t('common:toast.success.refresh'),
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(fetchData);
    } else {
      toast.dismiss(toastIdRef.current);
    }
    setLoading(false);
  };

  const {
    canDelete,
    canDeleteBulk,
    toDelete,
    toDeleteBulk,
    setToDelete,
    setToDeleteBulk,
    handleDelete,
    handleBulkDelete,
    requestRowDelete,
    requestSelectionDelete,
    isConfirmOpen,
  } = useVoucherDelete(fetchData, toastIdRef);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = await loadFromIndexedDB();
      if (cancelled || firstFetchRef.current) return;
      firstFetchRef.current = true;
      if (!loaded) void fetchData();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sharedBatches = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of data || []) {
      const batch = String(row.batch || '');
      if (!batch) continue;
      counts.set(batch, (counts.get(batch) || 0) + 1);
    }
    return counts;
  }, [data]);

  const columns = useMemo(
    () => voucherColumns(t, {
      onCodeClick: (row) => setToView(row),
      onBatchClick: (row) => setToViewBatch(String(row.batch || '')),
      batchCounts: sharedBatches,
    }),
    [t, sharedBatches],
  );

  const isSharedBatch = (row: VoucherRow) =>
    Boolean(row.batch) && (sharedBatches.get(String(row.batch)) || 0) > 1;

  const setStatus = async (row: VoucherRow, status: string) => {
    const res = await api.post(`/vouchers/${row.id}/status`, { status });
    if (res?.status === 204) {
      toast.success(t('common:toast.success.voucherUpdate'), { autoClose: 2500 });
      void fetchData();
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(() => setStatus(row, status));
    }
  };

  const revokeBatch = async () => {
    if (!toRevokeBatch) return;
    const batch = toRevokeBatch;
    const res = await api.post(`/vouchers/batch/${encodeURIComponent(batch)}/status`, { status: 'REVOKED' });
    if (res?.status === 200) {
      setToRevokeBatch(null);
      toast.success(t('common:toast.success.voucherBatchRevoked'), { autoClose: 2500 });
      void fetchData();
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(revokeBatch);
    } else {
      setToRevokeBatch(null);
    }
  };

  const deleteBatch = async () => {
    if (!toDeleteBatch) return;
    const batch = toDeleteBatch;
    setLoadingMessage('DELETE');
    const res = await api.delete(`/vouchers/batch/${encodeURIComponent(batch)}`);
    setLoadingMessage(null);
    if (res?.status === 200) {
      setToDeleteBatch(null);
      toastIdRef.current = toast.loading(
        `${t('common:toast.success.delete')} ${t('common:toast.loading.refresh')}`,
      );
      void fetchData();
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(deleteBatch);
    } else {
      setToDeleteBatch(null);
    }
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TableView
				stateKey={LocalStorageKeys.VOUCHER_RECORDS}
				posting="voucher"
          dataScope="voucher"
          header={t('common:voucher.title')}
          data={data}
          columns={columns}
          lastUpdated={lastUpdate}
          period={period}
          setDateRangeFilter={setPeriod}
          isLoading={loading}
          hideColumns={['batch']}
          totals={[{ label: t('common:tables.amount'), accessorFn: (row: VoucherRow) => Number(row.amount || 0) }]}
          renderTopToolbarActions={(table) => (
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Tooltip title={t('common:ui.tooltips.refreshData')}>
                <IconButton onClick={() => void fetchData()} disabled={loading} color="primary">
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canPost && (
                <Tooltip title={t('common:voucher.postTitle')}>
                  <IconButton color="success" onClick={() => { setEditing(null); setPostOpen(true); }}>
                    <AddCircleOutline />
                  </IconButton>
                </Tooltip>
              )}
              {canEdit && table.getSelectedRowModel().flatRows.length === 1 && (
                <Tooltip title={t('common:ui.tooltips.editSelectedVoucher')}>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setEditing(table.getSelectedRowModel().flatRows[0].original);
                      setPostOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {canDeleteBulk && (table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
                <Tooltip title={t('common:ui.tooltips.deleteSelection')}>
                  <IconButton color="error" onClick={() => requestSelectionDelete(table)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
              <ExportMenuButton
                prefix="vouchers"
                data={data}
                columns={columns.filter((column) => column.accessorKey !== 'batch')}
                table={table}
              />
            </Box>
          )}
          renderRowActionMenuItems={(row, closeMenu) => [
            ...(isSharedBatch(row.original) ? [
              <MenuItem
                key="view-batch"
                onClick={() => {
                  setToViewBatch(row.original.batch as string);
                  closeMenu();
                }}
              >
                <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                {t('common:voucher.viewBatch')}
              </MenuItem>,
            ] : []),
            canEdit ? (
              <MenuItem
                key="edit"
                onClick={() => {
                  setEditing(row.original);
                  setPostOpen(true);
                  closeMenu();
                }}
                sx={{ color: 'primary.main' }}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}><Edit fontSize="small" /></ListItemIcon>
                {t('common:ui.buttons.edit')}
              </MenuItem>
            ) : null,
            ...(canEdit
              ? VOUCHER_STATUSES.filter((status) => status !== (row.original.status || 'ACTIVE')).map((status) => (
                <MenuItem
                  key={`status-${status}`}
                  onClick={() => {
                    void setStatus(row.original, status);
                    closeMenu();
                  }}
                >
                  {t('common:voucher.setStatus', { status: t(voucherStatusI18nKey(status)) })}
                </MenuItem>
              ))
              : []),
            canEdit && row.original.status !== 'REVOKED' ? (
              <MenuItem
                key="revoke"
                onClick={() => {
                  setToRevoke(row.original);
                  closeMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}><Block fontSize="small" /></ListItemIcon>
                {t('common:voucher.revoke')}
              </MenuItem>
            ) : null,
            canEdit && isSharedBatch(row.original) ? (
              <MenuItem
                key="revoke-batch"
                onClick={() => {
                  setToRevokeBatch(row.original.batch as string);
                  closeMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}><Block fontSize="small" /></ListItemIcon>
                {t('common:voucher.revokeBatch')}
              </MenuItem>
            ) : null,
            canDeleteBulk && isSharedBatch(row.original) ? (
              <MenuItem
                key="delete-batch"
                onClick={() => {
                  setToDeleteBatch(row.original.batch as string);
                  closeMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}><Delete fontSize="small" /></ListItemIcon>
                {t('common:voucher.deleteBatch')}
              </MenuItem>
            ) : null,
            canDelete ? (
              <MenuItem
                key="delete"
                onClick={() => {
                  requestRowDelete(row.original);
                  closeMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}><Delete fontSize="small" /></ListItemIcon>
                {t('common:ui.buttons.delete')}
              </MenuItem>
            ) : null,
          ]}
        />
      </LocalizationProvider>

      <VoucherModal
        isOpen={postOpen}
        voucher={editing}
        onClose={() => { setPostOpen(false); setEditing(null); }}
        onSuccess={() => void fetchData()}
      />

      {toView && (
        <VoucherReport
          isOpen={!!toView}
          toggle={() => setToView(null)}
          bundle={toView}
        />
      )}

      {toViewBatch && (
        <VoucherBatchReport
          isOpen={!!toViewBatch}
          toggle={() => setToViewBatch(null)}
          batch={toViewBatch}
        />
      )}

      <ConfirmDialog
        onConfirm={toDelete ? handleDelete : handleBulkDelete}
        onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]); }}
        isOpen={isConfirmOpen}
        setIsOpen={() => {}}
        title={<><DeleteOutline sx={{ color: 'red' }} /> {t('common:dialog.deleteConfirmation')}</>}
        message={
          toDelete ? (
            <>
              {t('common:voucher.confirm.deleteSingle')}&nbsp;
              <b><u>{toDelete.code}</u></b>&nbsp;
              {t('common:voucher.confirm.deleteSingleAmount')} <b><u>{Util.formatNumber(toDelete.amount)}</u></b>
            </>
          ) : (
            <>{t('common:voucher.confirm.deleteBulk', { count: Number(toDeleteBulk.length) })}</>
          )
        }
        confirmBtnColor="error"
        confirmBtnText={t('common:ui.buttons.delete')}
      />

      <ConfirmDialog
        onConfirm={() => {
          if (toRevoke) void setStatus(toRevoke, 'REVOKED');
          setToRevoke(null);
        }}
        onCancel={() => setToRevoke(null)}
        isOpen={Boolean(toRevoke)}
        setIsOpen={() => {}}
        title={t('common:voucher.revoke')}
        message={
          toRevoke ? (
            <>{t('common:voucher.confirm.revokeSingle')} <b><u>{toRevoke.code}</u></b></>
          ) : ''
        }
        confirmBtnColor="error"
        confirmBtnText={t('common:voucher.revoke')}
      />

      <ConfirmDialog
        onConfirm={() => void revokeBatch()}
        onCancel={() => setToRevokeBatch(null)}
        isOpen={Boolean(toRevokeBatch)}
        setIsOpen={() => {}}
        title={t('common:voucher.revokeBatch')}
        message={t('common:voucher.confirm.revokeBatch')}
        confirmBtnColor="error"
        confirmBtnText={t('common:voucher.revokeBatch')}
      />

      <ConfirmDialog
        onConfirm={() => void deleteBatch()}
        onCancel={() => setToDeleteBatch(null)}
        isOpen={Boolean(toDeleteBatch)}
        setIsOpen={() => {}}
        title={t('common:voucher.deleteBatch')}
        message={t('common:voucher.confirm.deleteBatch')}
        confirmBtnColor="error"
        confirmBtnText={t('common:voucher.deleteBatch')}
      />
    </>
  );
};

export default Voucher;
